import type { ResearchEntryCoverageSummary } from '@einhaus/project314-types';
import { Callout, DataTable, Disclosure } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';

type Group = ResearchEntryCoverageSummary['byAsset'][number];
const labels: Record<Group['reason'], string> = {
    invalidPrice: 'Price-quality hold',
    confirmedMissing: 'Confirmed missing history',
    repairPending: 'History repair pending',
    unavailableRequiredEvidence: 'Required history unavailable'
};
const columns: ColumnDef<Group>[] = [
    { accessorKey: 'symbol', header: 'Symbol' },
    { accessorKey: 'duration', header: 'Timeframe' },
    { id: 'reason', header: 'Reason', cell: ({ row }) => labels[row.original.reason] },
    { accessorKey: 'excludedEvaluationCount', header: 'Excluded evaluations' },
    { accessorKey: 'firstSessionDate', header: 'First affected session' },
    { accessorKey: 'lastSessionDate', header: 'Last affected session' }
];

export const InputCoverageCallout = ({ coverage }: { coverage: ResearchEntryCoverageSummary }) => {
    if (coverage.excludedEvaluationCount === 0) return null;
    return (
        <Callout className="mb-6" title="Research input exclusions" variant="warning">
            <p>
                {coverage.excludedEvaluationCount.toLocaleString()} input evaluations excluded across {coverage.affectedAssetCount} assets,{' '}
                {coverage.firstSessionDate}–{coverage.lastSessionDate}.
            </p>
            <p className="mt-1 text-sm">
                Counts are asset/timeframe/session evaluations. Missing input can affect later lookback windows; these are not counts of
                missing source bars or missed trades. Unresolved outcomes after entry remain separate.
            </p>
            <Disclosure className="mt-3" title="Affected symbols and reasons">
                <DataTable columns={columns} data={coverage.byAsset} />
                {coverage.truncated && (
                    <p className="mt-2 text-sm">
                        Showing the {coverage.byAsset.length.toLocaleString()} largest groups of {coverage.groupCount.toLocaleString()}.
                        Totals and date ranges include all groups.
                    </p>
                )}
            </Disclosure>
        </Callout>
    );
};
